import React, { useMemo, useState } from 'react';
import { useGallery } from '../context/GalleryContext';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useUploadQueue } from '../hooks/useUploadQueue';

const VISIBLE_STATUSES = new Set(['pending', 'failed', 'paused', 'uploading', 'done']);
const RETRYABLE_STATUSES = ['pending', 'failed', 'paused'];
const ACTION_BUTTON_CLASSES =
  'inline-flex min-h-11 items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-9 sm:py-1.5 sm:text-xs';

function pluralizeUpload(count) {
  return count === 1 ? 'upload' : 'uploads';
}

function attentionNeededText(count) {
  return `Attention needed for ${count} ${pluralizeUpload(count)} - open details to retry`;
}

function getStatusLabel(item) {
  if (item.status === 'uploading') {
    return `Uploading${typeof item.progress === 'number' ? ` ${item.progress}%` : ''}`;
  }

  if (item.status === 'failed') {
    if (item.policyBlocked) {
      return 'Unavailable';
    }
    return item.retryExhausted ? 'Retry needed' : 'Failed';
  }

  if (item.status === 'paused') {
    return 'Paused';
  }

  if (item.status === 'done') {
    return 'Complete';
  }

  return 'Pending';
}

function getStatusClasses(status) {
  if (status === 'failed') {
    return 'border-red-200 bg-red-50 text-red-700';
  }

  if (status === 'uploading') {
    return 'border-primary bg-primary-10 text-secondary';
  }

  if (status === 'done') {
    return 'border-accent bg-accent-30 text-secondary';
  }

  return 'border-accent-30 bg-neutral-30 text-secondary';
}

export default function OfflineUploadStatus() {
  const { galleryId } = useGallery();
  const { isOnline } = useNetworkStatus();
  const {
    queue: allQueue,
    isProcessing,
    isReady,
    processQueue,
    retryItem,
    removeItem,
    clearCompleted,
    removeItemsForGallery,
  } = useUploadQueue();
  const queue = useMemo(() => allQueue.filter(item => item.galleryId === galleryId), [allQueue, galleryId]);
  const pendingCount = queue.filter(item => ['pending', 'failed', 'paused'].includes(item.status)).length;
  const [isExpanded, setIsExpanded] = useState(false);
  const [panelError, setPanelError] = useState('');
  const [isRetryAllPending, setIsRetryAllPending] = useState(false);
  const [pendingRowActionId, setPendingRowActionId] = useState(null);
  const [pendingBulkAction, setPendingBulkAction] = useState(null);

  const visibleItems = useMemo(
    () => queue.filter((item) => VISIBLE_STATUSES.has(item.status)),
    [queue]
  );
  const uploadingCount = useMemo(
    () => queue.filter((item) => item.status === 'uploading').length,
    [queue]
  );
  const completedCount = useMemo(
    () => queue.filter((item) => item.status === 'done').length,
    [queue]
  );
  const pausedCount = useMemo(
    () => queue.filter((item) => item.status === 'paused').length,
    [queue]
  );
  const policyBlockedCount = useMemo(
    () => queue.filter((item) => item.status === 'failed' && item.policyBlocked).length,
    [queue]
  );
  const galleryCancelableCount = useMemo(
    () => queue.filter(
      (item) =>
        galleryId &&
        item.galleryId === galleryId &&
        RETRYABLE_STATUSES.includes(item.status)
    ).length,
    [galleryId, queue]
  );
  const retryAllCount = useMemo(() => {
    return queue.filter((item) => {
      if (item.status === 'pending') {
        return true;
      }

      if (item.status === 'paused') {
        return true;
      }

      return item.status === 'failed' && !item.retryExhausted;
    }).length;
  }, [queue]);
  const drainReadyCount = useMemo(() => {
    const currentTimestamp = Date.now();

    return queue.filter((item) => {
      if (item.status === 'pending') {
        return true;
      }

      return (
        item.status === 'failed' &&
        !item.retryExhausted &&
        (!item.retryAfter || item.retryAfter <= currentTimestamp)
      );
    }).length;
  }, [queue]);

  const hasQueueWork = visibleItems.length > 0;

  if (isOnline && !hasQueueWork) {
    return null;
  }

  const activeCount = uploadingCount + drainReadyCount;
  const remainingCount = pendingCount + uploadingCount;
  const canShowQueuePanel = isReady && visibleItems.length > 0;
  const hasActionPending = pendingRowActionId !== null || pendingBulkAction !== null;
  const canRetryAll =
    isReady &&
    isOnline &&
    retryAllCount > 0 &&
    !isProcessing &&
    !isRetryAllPending &&
    !hasActionPending;
  const hasPendingAttention = pendingCount > 0;
  const hasExhaustedAttention = pendingCount > retryAllCount;
  const isCompletedOnly = completedCount > 0 && pendingCount === 0 && uploadingCount === 0;
  const showCompletedClear = isReady && isCompletedOnly && !isExpanded;
  const showActionRow =
    isReady && (canShowQueuePanel || retryAllCount > 0 || showCompletedClear);
  const hasTwoSummaryActions =
    canShowQueuePanel && (retryAllCount > 0 || showCompletedClear);
  let statusText = hasQueueWork
    ? 'Queued uploads are updating...'
    : 'Upload status is up to date';

  if (!isReady) {
    statusText = 'Checking upload queue...';
  } else if (!isOnline && pausedCount > 0) {
    statusText = `Offline - attention needed for ${pausedCount} paused ${pluralizeUpload(pausedCount)}`;
  } else if (!isOnline && retryAllCount > 0) {
    statusText = `Offline - ${retryAllCount} ${pluralizeUpload(retryAllCount)} will resume when reconnected`;
  } else if (!isOnline && hasPendingAttention) {
    statusText = `Offline - attention needed for ${pendingCount} ${pluralizeUpload(pendingCount)}`;
  } else if (!isOnline && (isProcessing || uploadingCount > 0)) {
    statusText = 'Offline - upload paused until reconnected';
  } else if (!isOnline) {
    statusText = 'Offline mode - cached photos available';
  } else if (isProcessing && activeCount > 0) {
    statusText = `${completedCount} of ${visibleItems.length} uploaded · ${visibleItems.length - completedCount} remaining`;
  } else if (policyBlockedCount > 0 && policyBlockedCount === pendingCount) {
    statusText = `${policyBlockedCount} queued ${pluralizeUpload(policyBlockedCount)} unavailable`;
  } else if (policyBlockedCount > 0 && retryAllCount > 0) {
    statusText = `${retryAllCount} ${pluralizeUpload(retryAllCount)} ready to retry; ${policyBlockedCount} unavailable`;
  } else if (policyBlockedCount > 0) {
    const otherAttentionCount = pendingCount - policyBlockedCount;
    const attentionVerb = otherAttentionCount === 1 ? 'needs' : 'need';
    statusText = `${policyBlockedCount} unavailable; ${otherAttentionCount} ${pluralizeUpload(otherAttentionCount)} ${attentionVerb} attention`;
  } else if (retryAllCount > 0 && hasExhaustedAttention) {
    statusText = `${retryAllCount} of ${pendingCount} ${pluralizeUpload(pendingCount)} ready to retry`;
  } else if (retryAllCount > 0) {
    statusText = `${retryAllCount} ${pluralizeUpload(retryAllCount)} ready to retry`;
  } else if (hasPendingAttention) {
    statusText = attentionNeededText(pendingCount);
  } else if (completedCount > 0) {
    statusText = `${completedCount} ${pluralizeUpload(completedCount)} completed`;
  }

  const handleRetryAll = async () => {
    if (!canRetryAll) {
      return;
    }

    setPanelError('');
    setIsRetryAllPending(true);
    try {
      const retryableItems = queue.filter(
        (item) => item.status === 'paused' ||
          (item.status === 'failed' && !item.retryExhausted)
      );
      for (const item of retryableItems) {
        await retryItem(item.id);
      }
      await processQueue();
    } catch (error) {
      setPanelError(error instanceof Error ? error.message : 'Unable to retry uploads.');
    } finally {
      setIsRetryAllPending(false);
    }
  };

  const handleRetryItem = async (item) => {
    setPanelError('');
    setPendingRowActionId(item.id);
    try {
      await retryItem(item.id);
    } catch (error) {
      setPanelError(error instanceof Error ? error.message : `Unable to retry ${item.fileName}.`);
    } finally {
      setPendingRowActionId(null);
    }
  };

  const handleRemoveItem = async (item) => {
    setPanelError('');
    setPendingRowActionId(item.id);
    try {
      await removeItem(item.id);
    } catch (error) {
      setPanelError(error instanceof Error ? error.message : `Unable to remove ${item.fileName}.`);
    } finally {
      setPendingRowActionId(null);
    }
  };

  const handleCancelUpload = async (item) => {
    setPanelError('');
    setPendingRowActionId(item.id);
    try {
      const cancelled = await removeItem(item.id, { force: true });
      if (!cancelled) {
        setPanelError(`Unable to cancel ${item.fileName}.`);
      }
    } catch (error) {
      setPanelError(error instanceof Error ? error.message : `Unable to cancel ${item.fileName}.`);
    } finally {
      setPendingRowActionId(null);
    }
  };

  const handleClearCompleted = async () => {
    setPanelError('');
    setPendingBulkAction('clear-completed');
    try {
      await clearCompleted(galleryId);
    } catch (error) {
      setPanelError(error instanceof Error ? error.message : 'Unable to clear completed uploads.');
    } finally {
      setPendingBulkAction(null);
    }
  };

  const handleCancelGalleryUploads = async () => {
    if (!galleryId || galleryCancelableCount === 0) {
      return;
    }

    setPanelError('');
    setPendingBulkAction('cancel-gallery');
    try {
      await removeItemsForGallery(galleryId, { statuses: RETRYABLE_STATUSES });
    } catch (error) {
      setPanelError(error instanceof Error ? error.message : 'Unable to cancel queued uploads.');
    } finally {
      setPendingBulkAction(null);
    }
  };

  const placementClasses =
    'bottom-[max(1rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] right-[calc(5rem+env(safe-area-inset-right))] sm:right-auto sm:w-[28rem]';
  const maxHeightClasses = isExpanded
    ? 'max-h-[calc(100dvh-5.5rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))]'
    : 'max-h-[calc(100dvh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))]';

  return (
    <>
      <div
        className="h-36 flex-none sm:h-32"
        role="presentation"
        aria-hidden="true"
      />
      <section
        className={`fixed z-20 flex flex-col overflow-hidden rounded-xl border border-accent bg-surface text-secondary shadow-lg ${placementClasses} ${maxHeightClasses}`}
        aria-label={isOnline ? 'Upload status' : 'Offline and upload status'}
      >
        <div className="flex min-h-0 w-full flex-col gap-2.5 p-3 sm:p-4">
        <div className="flex items-center gap-3">
          <div
            className="relative flex h-11 w-11 flex-none items-center justify-center rounded-full bg-accent-30 text-secondary"
            aria-hidden="true"
          >
            {isOnline && isProcessing ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-accent border-t-primary motion-reduce:animate-none" />
            ) : isCompletedOnly ? (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6H16a5 5 0 011 9.9M12 10v8m0-8l-3 3m3-3l3 3" />
              </svg>
            )}
            {remainingCount > 0 ? (
              <span title={`${remainingCount} ${pluralizeUpload(remainingCount)} remaining`} className="absolute -right-1.5 -top-1.5 min-w-5 rounded-full bg-primary px-1 text-center text-[10px] font-bold leading-5 text-surface">
                {remainingCount}
              </span>
            ) : null}
          </div>
          <p
            className="min-w-0 flex-1 text-sm font-medium leading-5"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {statusText}
          </p>
        </div>

        {showActionRow ? (
          <div className={`grid gap-2 ${hasTwoSummaryActions ? 'grid-cols-2' : ''}`}>
            {canShowQueuePanel ? (
              <button
                type="button"
                className={`${ACTION_BUTTON_CLASSES} w-full border-accent bg-surface text-secondary hover:bg-accent-20`}
                aria-expanded={isExpanded}
                aria-controls="offline-upload-status-panel"
                aria-label={isExpanded ? 'Hide upload queue' : 'Show upload queue'}
                onClick={() => setIsExpanded((current) => !current)}
              >
                {isExpanded ? 'Hide' : 'Details'}
              </button>
            ) : null}
            {retryAllCount > 0 ? (
              <button
                type="button"
                className={`${ACTION_BUTTON_CLASSES} w-full border-primary bg-primary text-surface hover:bg-primary-90`}
                style={{ color: 'var(--color-surface)' }}
                onClick={handleRetryAll}
                disabled={!canRetryAll}
                aria-label="Retry all queued uploads"
              >
                {isProcessing || isRetryAllPending ? 'Uploading...' : 'Retry all'}
              </button>
            ) : null}
            {showCompletedClear ? (
              <button
                type="button"
                className={`${ACTION_BUTTON_CLASSES} w-full border-accent bg-accent-20 text-secondary hover:bg-accent-30`}
                onClick={handleClearCompleted}
                disabled={hasActionPending}
                aria-label="Clear completed uploads"
              >
                Clear completed
              </button>
            ) : null}
          </div>
        ) : null}

        {panelError ? (
          <p className="text-sm font-medium text-red-700" role="alert">
            {panelError}
          </p>
        ) : null}

        {!isReady ? null : isExpanded && visibleItems.length > 0 ? (
          <div
            id="offline-upload-status-panel"
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-accent-30 bg-surface shadow-sm"
          >
            <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:max-h-[40dvh]">
              <ul className="space-y-2.5">
                {visibleItems.map((item) => {
                  const isUploading = item.status === 'uploading';
                  const isFailed = item.status === 'failed';
                  const isPaused = item.status === 'paused';
                  const isDone = item.status === 'done';
                  const progress = typeof item.progress === 'number'
                    ? Math.min(100, Math.max(0, item.progress))
                    : 0;

                  return (
                    <li
                      key={item.id}
                      className="rounded-lg border border-accent-30 bg-surface p-3 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-secondary">{item.fileName}</p>
                          <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${getStatusClasses(item.status)}`}>
                            {getStatusLabel(item)}
                          </span>
                          {isFailed ? (
                            <p className="mt-1 break-words text-xs text-red-700">{item.error || 'Upload failed'}</p>
                          ) : null}
                        </div>
                      </div>

                      {isUploading ? (
                        <div
                          className="mt-2 h-1.5 overflow-hidden rounded-full bg-accent-30"
                          role="progressbar"
                          aria-label={`Upload progress for ${item.fileName}`}
                          aria-valuemin="0"
                          aria-valuemax="100"
                          aria-valuenow={progress}
                        >
                          <div
                            className="h-full rounded-full bg-primary transition-[width] duration-200 motion-reduce:transition-none"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      ) : null}

                      {!isDone ? (
                        <div className="mt-3 flex w-full items-center gap-2 sm:justify-end">
                          {(isFailed && !item.policyBlocked) || isPaused ? (
                            <button
                              type="button"
                              className={`${ACTION_BUTTON_CLASSES} flex-1 border-primary bg-primary text-surface hover:bg-primary-90 sm:flex-none`}
                              style={{ color: 'var(--color-surface)' }}
                              onClick={() => handleRetryItem(item)}
                              disabled={!isOnline || isProcessing || hasActionPending}
                              aria-label={`${isPaused ? 'Resume' : 'Retry'} ${item.fileName}`}
                            >
                              {isPaused ? 'Resume' : 'Retry'}
                            </button>
                          ) : null}
                          {isUploading ? (
                            <button
                              type="button"
                              className={`${ACTION_BUTTON_CLASSES} flex-1 border-red-300 bg-red-50 text-red-700 hover:bg-red-100 sm:flex-none`}
                              onClick={() => handleCancelUpload(item)}
                              disabled={hasActionPending}
                              aria-label={`Cancel upload ${item.fileName}`}
                            >
                              Cancel upload
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={`${ACTION_BUTTON_CLASSES} flex-1 border-accent bg-surface text-secondary hover:bg-accent-20 sm:flex-none`}
                              onClick={() => handleRemoveItem(item)}
                              disabled={hasActionPending}
                              aria-label={`Remove ${item.fileName}`}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
            {completedCount > 0 || galleryCancelableCount > 0 ? (
              <div className="grid gap-2 border-t border-accent-30 bg-surface p-3 xs:grid-cols-2 sm:flex sm:justify-end">
                {completedCount > 0 ? (
                  <button
                    type="button"
                    className={`${ACTION_BUTTON_CLASSES} w-full border-accent bg-surface text-secondary hover:bg-accent-20 sm:w-auto`}
                    onClick={handleClearCompleted}
                    disabled={hasActionPending}
                    aria-label="Clear completed uploads"
                  >
                    Clear completed
                  </button>
                ) : null}
                {galleryCancelableCount > 0 ? (
                  <button
                    type="button"
                    className={`${ACTION_BUTTON_CLASSES} w-full border-red-300 bg-red-50 text-red-700 hover:bg-red-100 sm:w-auto`}
                    onClick={handleCancelGalleryUploads}
                    disabled={isProcessing || hasActionPending}
                    aria-label="Cancel queued uploads for this gallery"
                  >
                    Cancel queued
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        </div>
      </section>
    </>
  );
}
